import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

export default function VerifyEmail() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { confirmEmail, resendVerificationCode } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await confirmEmail(email, code.trim());
      setSuccess('Email verified successfully!');
      timerRef.current = setTimeout(() => {
        navigate('/login', { state: { message: 'Email verified. Please sign in.' } });
      }, 1500);
    } catch (err) {
      console.error('[VerifyEmail] Error:', err);
      const messages = {
        CodeMismatchException: 'Invalid verification code.',
        ExpiredCodeException: 'Code has expired. Please request a new one.',
        NotAuthorizedException: 'Verification failed. Your account may already be verified — try signing in.',
        LimitExceededException: 'Too many attempts. Please wait a few minutes.',
      };
      setError(messages[err.code] || err.message || 'Verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await resendVerificationCode(email);
      setSuccess('New verification code sent to your email.');
      setResendCooldown(60);
    } catch (err) {
      console.error('[VerifyEmail] Resend error:', err);
      const messages = {
        UserNotFoundException: 'No account found with this email.',
        InvalidParameterException: 'This account is already verified. Please sign in.',
        LimitExceededException: 'Too many attempts. Please wait a few minutes.',
      };
      setError(messages[err.code] || 'Failed to resend code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Verify Email</h1>
        <p className="auth-subtitle">
          Enter the verification code sent to your email
        </p>

        {error && <div className="auth-error">{error}</div>}
        {success && <div className="auth-success">{success}</div>}

        <form onSubmit={handleVerify}>
          {!location.state?.email && (
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter 6-digit code"
              autoComplete="one-time-code"
            />
          </div>

          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="auth-links">
          <span>Didn't receive the code? </span>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isLoading || resendCooldown > 0}
            style={{
              background: 'none',
              border: 'none',
              color: resendCooldown > 0 ? 'var(--text-muted)' : 'var(--primary-color)',
              cursor: resendCooldown > 0 ? 'default' : 'pointer',
              fontSize: '14px',
              padding: 0,
              textDecoration: resendCooldown > 0 ? 'none' : 'underline',
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </button>
          <span className="separator">|</span>
          <Link to="/login">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}
